import type { ImageQualityMode, SceneImageMeta, SceneImageVariants } from '../types'

type ImageTransformOptions = {
  width?: number
  height?: number
  fit?: 'cover' | 'scale-down' | 'contain'
  quality?: number
  format?: 'auto'
}

type PanoramaVariantKey = keyof SceneImageVariants

const PANORAMA_VARIANTS: Record<PanoramaVariantKey, { width: number; quality: number }> = {
  preview: { width: 1024, quality: 64 },
  fast: { width: 2048, quality: 82 },
  standard: { width: 4096, quality: 88 },
  high: { width: 6144, quality: 90 },
  best: { width: 8192, quality: 92 },
}

function buildTransformUrl(url: string, options: ImageTransformOptions): string {
  if (!url || url.includes('/cdn-cgi/image/') || url.startsWith('data:') || url.startsWith('blob:')) return url

  const params: string[] = []
  if (options.width) params.push(`width=${Math.round(options.width)}`)
  if (options.height) params.push(`height=${Math.round(options.height)}`)
  if (options.fit) params.push(`fit=${options.fit}`)
  if (options.quality) params.push(`quality=${Math.round(options.quality)}`)
  if (options.format) params.push(`format=${options.format}`)
  if (params.length === 0) return url

  try {
    const parsed = new URL(url)
    return `${parsed.origin}/cdn-cgi/image/${params.join(',')}${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

export function normalizeImageQualityMode(value: unknown): ImageQualityMode {
  return value === 'fast' || value === 'best' ? value : 'auto'
}

function getWebglMaxTextureSize(): number {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null
    return gl?.getParameter(gl.MAX_TEXTURE_SIZE) || 4096
  } catch {
    return 4096
  }
}

function connectionPrefersSmallImages(): boolean {
  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string }
  }).connection
  if (!connection) return false
  if (connection.saveData) return true
  return ['slow-2g', '2g'].includes(String(connection.effectiveType || '').toLowerCase())
}

function sourceWidthLimit(imageMeta?: SceneImageMeta | null): number {
  const width = Number(imageMeta?.width || 0)
  return Number.isFinite(width) && width > 0 ? width : Number.POSITIVE_INFINITY
}

function textureWidthLimit(maxTextureSize?: number): number {
  const textureLimit = maxTextureSize || getWebglMaxTextureSize()
  return textureLimit >= 8192 ? 8192 : 4096
}

function capWidth(width: number, maxTextureSize?: number, imageMeta?: SceneImageMeta | null): number {
  const sourceLimit = sourceWidthLimit(imageMeta)
  const minimum = Number.isFinite(sourceLimit) ? Math.min(1024, sourceLimit) : 1024
  return Math.max(minimum, Math.round(Math.min(width, textureWidthLimit(maxTextureSize), sourceLimit)))
}

function variantForWidth(width: number): PanoramaVariantKey {
  if (width <= PANORAMA_VARIANTS.preview.width) return 'preview'
  if (width <= PANORAMA_VARIANTS.fast.width) return 'fast'
  if (width <= PANORAMA_VARIANTS.standard.width) return 'standard'
  if (width <= PANORAMA_VARIANTS.high.width) return 'high'
  return 'best'
}

function variantUrl(imageMeta: SceneImageMeta | null | undefined, key: PanoramaVariantKey): string | null {
  const url = imageMeta?.variants?.[key]
  return typeof url === 'string' && url.trim() ? url : null
}

function masterUrl(fallbackUrl: string, imageMeta?: SceneImageMeta | null): string {
  return imageMeta?.master_url || fallbackUrl
}

export function getPanoramaTargetWidth(
  maxTextureSize?: number,
  mode: ImageQualityMode = 'auto',
  imageMeta?: SceneImageMeta | null
): number {
  if (mode === 'fast') return capWidth(PANORAMA_VARIANTS.fast.width, maxTextureSize, imageMeta)
  if (mode === 'best') return capWidth(PANORAMA_VARIANTS.best.width, maxTextureSize, imageMeta)

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const viewportPixels = Math.max(window.innerWidth, window.innerHeight) * dpr
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 8

  if (connectionPrefersSmallImages() || deviceMemory <= 2) {
    return capWidth(PANORAMA_VARIANTS.fast.width, maxTextureSize, imageMeta)
  }

  let width = 4096
  if (viewportPixels >= 2200 && deviceMemory > 4) width = 6144
  if (viewportPixels >= 3200 && deviceMemory >= 8) width = 8192

  return capWidth(width, maxTextureSize, imageMeta)
}

export function getOptimizedPanoramaUrl(
  url: string,
  maxTextureSize?: number,
  mode: ImageQualityMode = 'auto',
  imageMeta?: SceneImageMeta | null
): string {
  const width = getPanoramaTargetWidth(maxTextureSize, mode, imageMeta)
  const key = variantForWidth(width)
  return variantUrl(imageMeta, key) || buildTransformUrl(masterUrl(url, imageMeta), {
    width,
    fit: 'scale-down',
    quality: PANORAMA_VARIANTS[key].quality,
    format: 'auto',
  })
}

export function getPreviewPanoramaUrl(url: string, imageMeta?: SceneImageMeta | null): string {
  return variantUrl(imageMeta, 'preview') || buildTransformUrl(masterUrl(url, imageMeta), {
    width: PANORAMA_VARIANTS.preview.width,
    fit: 'scale-down',
    quality: PANORAMA_VARIANTS.preview.quality,
    format: 'auto',
  })
}

export function getOptimizedThumbnailUrl(url: string, width = 320, height = 200): string {
  return buildTransformUrl(url, {
    width,
    height,
    fit: 'cover',
    quality: 72,
    format: 'auto',
  })
}
